import { useState, useEffect, useMemo, useCallback } from 'react'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import {
    parseExpression,
    validateExpression,
    extractTags,
    legacyTagsToExpression,
    formatExpression,
} from '@/lib/tagExpression'
import { AlertCircle, Check, HelpCircle } from 'lucide-react'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'

interface TagExpressionInputProps {
    value: string
    onChange: (value: string) => void
    /** Legacy required_tags for conversion */
    legacyRequired?: string[]
    /** Legacy required_tags_any for conversion */
    legacyRequiredAny?: string[]
    /** Legacy exclude_tags for conversion */
    legacyExcluded?: string[]
    /** Label for the input */
    label?: string
    /** Placeholder text */
    placeholder?: string
    /** Additional class names */
    className?: string
    /** Disable the input */
    disabled?: boolean
}

const SYNTAX_HELP = `Tag Expression Syntax:
• "tag" - match tag (quotes optional)
• tag1 AND tag2 - both required
• tag1 OR tag2 - at least one required
• NOT tag - must not be present
• (expr) - grouping

Example: "watch" AND NOT "spam"`

export function TagExpressionInput({
    value,
    onChange,
    legacyRequired,
    legacyRequiredAny,
    legacyExcluded,
    label = 'Tag Expression',
    placeholder = 'e.g., "watch" AND NOT "spam"',
    className,
    disabled = false,
}: TagExpressionInputProps) {
    const [localValue, setLocalValue] = useState(value || '')
    const [showLegacyConvert, setShowLegacyConvert] = useState(false)
    const [showHelp, setShowHelp] = useState(false)

    // Check if legacy tags exist and expression is empty
    useEffect(() => {
        const hasLegacy = (legacyRequired?.length || 0) > 0 ||
            (legacyRequiredAny?.length || 0) > 0 ||
            (legacyExcluded?.length || 0) > 0
        const isEmpty = !value || !value.trim()
        setShowLegacyConvert(hasLegacy && isEmpty)
    }, [legacyRequired, legacyRequiredAny, legacyExcluded, value])

    // Sync local value with prop
    useEffect(() => {
        setLocalValue(value || '')
    }, [value])

    // Validate the expression
    const validation = useMemo(() => {
        if (!localValue.trim()) return { valid: true, isEmpty: true }
        return { ...validateExpression(localValue), isEmpty: false }
    }, [localValue])

    // Extract tags for preview
    const { included, excluded } = useMemo(() => {
        if (!localValue.trim() || !validation.valid) {
            return { included: [], excluded: [] }
        }
        const result = parseExpression(localValue)
        if (!result.success) return { included: [], excluded: [] }
        return extractTags(result.ast)
    }, [localValue, validation.valid])

    // Format for syntax highlighting
    const formattedTokens = useMemo(() => {
        if (!localValue.trim()) return []
        return formatExpression(localValue)
    }, [localValue])

    // Handlers
    const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value
        setLocalValue(newValue)
        onChange(newValue)
    }, [onChange])

    const handleConvertLegacy = useCallback(() => {
        const expression = legacyTagsToExpression(
            legacyRequired,
            legacyRequiredAny,
            legacyExcluded
        )
        setLocalValue(expression)
        onChange(expression)
        setShowLegacyConvert(false)
    }, [legacyRequired, legacyRequiredAny, legacyExcluded, onChange])

    // Render formatted preview
    const renderFormattedPreview = () => {
        if (!formattedTokens.length) return null

        return (
            <div className="font-mono text-sm bg-muted/30 rounded px-2 py-1 whitespace-pre-wrap break-all">
                {formattedTokens.map((token, idx) => (
                    <span
                        key={idx}
                        className={cn(
                            token.type === 'keyword' && 'text-blue-500 font-semibold',
                            token.type === 'tag' && 'text-amber-500',
                            token.type === 'paren' && 'text-muted-foreground',
                        )}
                    >
                        {token.text}
                    </span>
                ))}
            </div>
        )
    }

    return (
        <div className={cn('space-y-2', className)}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Label>{label}</Label>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5"
                                    onClick={() => setShowHelp(!showHelp)}
                                    type="button"
                                >
                                    <HelpCircle className="h-3.5 w-3.5 text-muted-foreground" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[280px]">
                                <pre className="text-xs whitespace-pre-wrap">{SYNTAX_HELP}</pre>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>

                {showLegacyConvert && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleConvertLegacy}
                        className="text-xs h-6"
                        type="button"
                    >
                        Convert legacy tags
                    </Button>
                )}
            </div>

            <div className="relative">
                <Textarea
                    value={localValue}
                    onChange={handleChange}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={cn(
                        'min-h-[60px] font-mono text-sm',
                        !validation.valid && 'border-destructive focus-visible:ring-destructive'
                    )}
                />

                {/* Validation indicator */}
                <div className="absolute right-2 top-2">
                    {!validation.isEmpty && (
                        validation.valid ? (
                            <Check className="h-4 w-4 text-green-500" />
                        ) : (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <AlertCircle className="h-4 w-4 text-destructive" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p className="text-sm">{validation.error}</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )
                    )}
                </div>
            </div>

            {/* Error message */}
            {!validation.valid && validation.error && (
                <p className="text-xs text-destructive">{validation.error}</p>
            )}

            {/* Syntax preview */}
            {validation.valid && localValue.trim() && (
                <div className="space-y-2">
                    {renderFormattedPreview()}

                    {/* Tag badges preview */}
                    {(included.length > 0 || excluded.length > 0) && (
                        <div className="flex flex-wrap gap-1">
                            {included.map((tag) => (
                                <Badge
                                    key={`inc-${tag}`}
                                    className="bg-green-500/20 text-green-600 dark:text-green-400 text-xs"
                                >
                                    +{tag}
                                </Badge>
                            ))}
                            {excluded.map((tag) => (
                                <Badge
                                    key={`exc-${tag}`}
                                    variant="destructive"
                                    className="text-xs"
                                >
                                    -{tag}
                                </Badge>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <p className="text-xs text-muted-foreground">
                Use AND, OR, NOT operators with quoted or unquoted tags
            </p>
        </div>
    )
}
